import { useState } from "react";
import api, { formatApiError, formatDate } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { FileMagnifyingGlass, CircleNotch, Warning, CheckCircle, Prohibit } from "@phosphor-icons/react";

const LEVEL_STYLES = {
  tinggi: "border-red-300 bg-red-50 text-red-800",
  sedang: "border-amber-300 bg-amber-50 text-amber-800",
  rendah: "border-yellow-200 bg-yellow-50 text-yellow-800",
};

export default function DocAnalysisSection({ claim, canRun, onChanged }) {
  const [running, setRunning] = useState(false);
  const [deciding, setDeciding] = useState(null);
  const analysis = claim.doc_analysis;

  const run = async () => {
    setRunning(true);
    try {
      const { data } = await api.post(`/claims/${claim.id}/analyze-documents`);
      toast.success(`Analisis dokumen selesai — ${data.temuan.length} temuan dari ${data.doc_count} dokumen`);
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  const decide = async (findingId, action) => {
    setDeciding(findingId);
    try {
      const { data } = await api.post(`/claims/${claim.id}/doc-findings/${findingId}`, { action });
      toast.success(action === "tambah" ? `Temuan ditambahkan ke skor — skor kini ${data.score}/100 (${data.risk_category})` : "Temuan diabaikan");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setDeciding(null); }
  };

  if (!canRun && !analysis) return null;

  return (
    <div data-testid="doc-analysis-section">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1.5">
          <FileMagnifyingGlass size={14} /> Analisis Dokumen (FraudGuard AI)
        </h3>
        {canRun && (
          <Button data-testid="run-doc-analysis-button" size="sm" disabled={running} onClick={run}
            className="bg-slate-900 hover:bg-slate-800 rounded-none h-8 gap-2">
            {running ? <><CircleNotch size={14} className="animate-spin" /> Membaca dokumen...</> : analysis ? "Analisis Ulang" : "Analisis Dokumen"}
          </Button>
        )}
      </div>

      {!analysis && <p className="text-sm text-muted-foreground border border-dashed border-border p-3">Belum dianalisis. FraudGuard AI akan membaca dokumen terunggah (gambar & PDF), membandingkannya dengan data klaim, dan melaporkan anomali/ketidaksesuaian material.</p>}

      {analysis && (
        <div className="border border-border/60 p-4 space-y-3" data-testid="doc-analysis-result">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm leading-relaxed">{analysis.ringkasan}</p>
            <p className="text-xs text-muted-foreground shrink-0">{analysis.doc_count} dokumen · {analysis.analyzed_by} · {formatDate(analysis.analyzed_at)}</p>
          </div>

          {analysis.temuan.length === 0 && (
            <div className="border border-emerald-200 bg-emerald-50 p-3 flex gap-2 text-sm text-emerald-800" data-testid="doc-analysis-clean">
              <CheckCircle size={17} className="shrink-0 mt-0.5" /> Tidak ditemukan anomali atau ketidaksesuaian material pada dokumen.
            </div>
          )}

          {analysis.temuan.map((t, i) => (
            <div key={t.id} data-testid={`doc-finding-${i}`} className={`border p-3 space-y-2 ${LEVEL_STYLES[t.tingkat] || LEVEL_STYLES.sedang}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-2">
                  <Warning size={17} className="shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold">{t.judul} <span className="font-mono text-xs">(+{t.poin_disarankan} poin disarankan)</span></p>
                    {t.dokumen && <p className="text-[11px] font-mono opacity-80">Dokumen: {t.dokumen}</p>}
                    <p className="text-xs mt-1 leading-relaxed">{t.penjelasan}</p>
                  </div>
                </div>
                {t.status !== "baru" && (
                  <span data-testid={`doc-finding-status-${i}`} className={`inline-flex items-center gap-1 shrink-0 px-2 py-0.5 text-[11px] font-semibold border ${t.status === "ditambahkan" ? "border-emerald-300 bg-emerald-100 text-emerald-800" : "border-slate-300 bg-slate-100 text-slate-600"}`}>
                    {t.status === "ditambahkan" ? <><CheckCircle size={12} /> Ditambahkan +{t.poin_disarankan}</> : <><Prohibit size={12} /> Diabaikan</>}
                  </span>
                )}
              </div>
              {t.status === "baru" && canRun && (
                <div className="flex gap-2 pl-6">
                  <Button data-testid={`add-finding-button-${i}`} size="sm" disabled={deciding === t.id}
                    onClick={() => decide(t.id, "tambah")}
                    className="bg-slate-900 hover:bg-slate-800 rounded-none h-7 text-xs px-3">
                    {deciding === t.id ? <CircleNotch size={12} className="animate-spin" /> : `Tambahkan ke Skoring (+${t.poin_disarankan})`}
                  </Button>
                  <Button data-testid={`ignore-finding-button-${i}`} size="sm" variant="outline" disabled={deciding === t.id}
                    onClick={() => decide(t.id, "abaikan")} className="rounded-none h-7 text-xs px-3 bg-white">
                    Abaikan
                  </Button>
                </div>
              )}
              {t.status !== "baru" && t.decided_by && (
                <p className="text-[11px] opacity-70 pl-6">Diputuskan oleh {t.decided_by} · {formatDate(t.decided_at)}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
